import { Link } from "react-router-dom";
import { LogOut, Menu, Shield, User, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { useEffect, useState } from "react";
import NavigationMenu from "./header/NavigationMenu";
import { Button } from "./ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "./ui/use-toast";
import { useNavigate } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import MobileNavBar from "./mobile/MobileNavBar";
import { useMobileNavigation } from "@/hooks/useMobileNavigation";
import { supabase } from "@/integrations/supabase/client";

const Header = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { isMobile, isMenuOpen, toggleMenu, closeMenu } = useMobileNavigation();

  useEffect(() => {
    const checkUserRole = async () => {
      if (!isAuthenticated) {
        setIsAdmin(false);
        return;
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const { data: roleData, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .single();

        if (error) {
          console.error("Error fetching role:", error);
          return;
        }

        const role = roleData?.role || "user";
        // Include 'editor' role when checking for admin dashboard access
        setIsAdmin(role === "admin" || role === "editor");
      } catch (error) {
        console.error("Error checking role:", error);
      }
    };

    checkUserRole();
  }, [isAuthenticated]);

  const handleLogout = async () => {
    if (isLoggingOut) return;

    try {
      setIsLoggingOut(true);
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account.",
      });

      navigate('/');
      
    } catch (error: any) {
      console.error("Logout error:", error);
      toast({
        title: "Error logging out",
        description: error.message || "An error occurred while logging out",
        variant: "destructive",
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-gradient-to-r from-hero-dark to-hero-mid px-4 py-1 md:py-2 flex justify-between items-center h-12 md:h-14">
        <Link to="/" className="flex items-center">
          <img 
            src="/lovable-uploads/598844d9-6dcc-4334-ba58-d470d9deaa33.png" 
            alt="METRADE Logo" 
            className="h-6 md:h-7 w-auto brightness-0 invert"
          />
        </Link>
        <div className="flex items-center gap-2">
          {/* Only show the authentication buttons on desktop */}
          {isAuthenticated ? (
            <div className="hidden md:flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/account" className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200">
                      <User className="h-4 w-4 text-white" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-white/10 backdrop-blur-sm text-white border-white/20">
                    <p>My Account</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              {isAdmin && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/admin" className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200">
                        <Shield className="h-4 w-4 text-white" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="bg-white/10 backdrop-blur-sm text-white border-white/20">
                      <p>Admin Dashboard</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button 
                      onClick={handleLogout}
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200"
                      disabled={isLoggingOut}
                    >
                      <LogOut className="h-4 w-4 text-white" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-white/10 backdrop-blur-sm text-white border-white/20">
                    <p>Logout</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              {!isLoading && (
                <>
                  <Link to="/auth">
                    <Button variant="outline" className="text-white bg-transparent border-white hover:bg-white/10">
                      Login
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button className="bg-white text-hero-dark hover:bg-white/90">
                      Sign Up
                    </Button>
                  </Link>
                </>
              )}
            </div>
          )}
          {/* Only show Sheet menu on desktop */}
          <div className="hidden md:block">
            <Sheet>
              <SheetTrigger asChild>
                <button className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200 ml-1">
                  <Menu className="h-4 w-4 text-white" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px] p-0 bg-gradient-to-b from-corporate-primary-100 to-corporate-primary-75 border-l border-white/10 shadow-xl">
                <NavigationMenu />
                <SheetClose className="absolute right-4 top-4 flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200">
                  <X className="h-4 w-4 text-white" />
                </SheetClose>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Bar */}
      {isMobile && (
        <MobileNavBar 
          isAuthenticated={isAuthenticated} 
          isAdmin={isAdmin}
          isLoading={isLoading}
          isMenuOpen={isMenuOpen}
          toggleMenu={toggleMenu}
          handleLogout={handleLogout}
        />
      )}
    </>
  );
};

export default Header;
